// Tables/cells · ActionButton
//
// Generic schema-driven action cell. Wraps `@ds/primitives/Button` with three
// extras every per-entity "action cell" was reinventing:
//
//   1. Auto-tracked pending state — when `onClick` returns a Promise, the cell
//      sets internal `loading: true` until it settles. External `loading`
//      prop OR-s with the internal one (so callers may also force-loading).
//   2. Self-hiding — when there is no work to do (`onClick` undefined AND
//      `disabled` true), the cell returns `null` instead of a greyed-out
//      button (these cells live in table rows where empty looks better than
//      "you can't click me").
//   3. Optional tooltip — wraps in AntD `Tooltip` only when `tooltip` is set.
//
// Absorbs:
//   - src/entities/transactions/cells/TransactionTransportActionCell.tsx
//   - src/entities/edpr/cells/EdprSendToBdoCell.tsx
//   - src/entities/edpr/cells/EdprGenerateRequestCell.tsx
//   - src/entities/transactions/cells/TransactionSendReminderCell.tsx
//
// The schema layer (`renderCell`) partial-applies `(record, extras)` into
// `props.onClick`, so every per-entity cell collapses to one schema row:
//
//   send_reminder: {
//     component: ActionButton,
//     data: (r) => ({
//       label: "Wyślij",
//       icon: <SendOutlined />,
//       size: "small",
//     }),
//     onClick: (r, extras) => (extras as TxExtras).actions.sendReminder(r.id),
//   }

import React, { useState } from "react";
import type { ReactNode } from "react";
import { Tooltip } from "antd";
import { Button } from "@ds/primitives/Button";
import type { ButtonVariant } from "@ds/primitives/Button";

// ─── Inputs (data props — the model) ─────────────────────────────────────

/** Tightened size scale — table-cell action buttons are always `small` or
 *  `middle` (mapped onto `@ds/primitives/Button` sm/md). */
export type ActionButtonSize = "small" | "default";

export interface ActionButtonInput {
  /** Visible label. Required — icon-only is the consumer's job to opt into
   *  by passing an empty string and an `icon`. */
  label: string;
  /** Leading icon. */
  icon?: ReactNode;
  /** Visual variant. Default `"default"` (outline neutral). */
  variant?: "primary" | "default" | "danger" | "text";
  /** Compact (`"small"`, default) or standard (`"default"`). */
  size?: ActionButtonSize;
  /** Force disabled — non-interactive + muted. */
  disabled?: boolean;
  /** Optional tooltip text. Renders AntD `Tooltip` only when set. */
  tooltip?: string;
  /** External loading override. OR-s with internal-pending state. */
  loading?: boolean;
  /** `data-testid` on the underlying button. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface ActionButtonOutput {
  /** Click handler. When it returns a Promise the cell tracks pending state
   *  internally. Click events are stopped from propagating to the row. */
  onClick?: () => void | Promise<void>;
}

export type ActionButtonProps = ActionButtonInput & ActionButtonOutput;

// ─── Variant mapping ─────────────────────────────────────────────────────

const VARIANT_TO_BUTTON: Record<NonNullable<ActionButtonInput["variant"]>, ButtonVariant> = {
  primary: "primary",
  default: "default",
  danger:  "danger",
  text:    "text",
};

const SIZE_TO_BUTTON: Record<ActionButtonSize, "sm" | "md"> = {
  small:   "sm",
  default: "md",
};

// ─── Implementation ──────────────────────────────────────────────────────

export function ActionButton({
  label,
  icon,
  variant = "default",
  size = "small",
  disabled,
  tooltip,
  loading,
  testId,
  onClick,
}: ActionButtonProps): ReactNode {
  const [internalLoading, setInternalLoading] = useState(false);

  // Self-hide: no work to do AND no affordance to display.
  if (!onClick && disabled) return null;

  const isLoading = !!loading || internalLoading;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!onClick) return;
    e.stopPropagation(); // table-row click bypass
    const result = onClick();
    if (result && typeof (result).then === "function") {
      setInternalLoading(true);
      (result).finally(() => setInternalLoading(false));
    }
  };

  const button = (
    <Button
      label={label}
      icon={icon}
      variant={VARIANT_TO_BUTTON[variant]}
      size={SIZE_TO_BUTTON[size]}
      disabled={disabled}
      loading={isLoading}
      onClick={handleClick}
      testId={testId}
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }
  return button;
}
